import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, CalendarDays, ExternalLink, Eye, Library, Loader2, Moon, PlayCircle, Search, Sparkles } from "lucide-react";
import { fadeUp, revealProps } from "@/lib/motion";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { API_BASE, LULLABY_CHANNEL_LINK } from "@/config/links";
import { trackClick } from "@/lib/track";
import { useSeo } from "@/lib/seo";

const PLACEHOLDER_COVER =
  "https://images.unsplash.com/photo-1502085671122-2d218cd434e6?crop=entropy&cs=srgb&fm=jpg&q=85";

function shortDate(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
  });
}

function monthKey(value) {
  return /^\d{4}-\d{2}/.test(value || "") ? value.slice(0, 7) : "unknown";
}

function monthLabel(key) {
  if (key === "unknown") return "Без даты";
  const [year, month] = key.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("ru-RU", {
    month: "long",
    year: "numeric",
  });
}

function formatViews(value) {
  return Number(value || 0).toLocaleString("ru-RU");
}

// fetch при недоступном API кидает TypeError "Failed to fetch" — латиницу пользователю не показываем.
function friendlyError(error, fallback) {
  const message = error?.message || "";
  return /[А-Яа-яЁё]/.test(message) ? message : fallback;
}

function groupLullabiesByMonth(lullabies) {
  const groups = new Map();
  for (const lullaby of lullabies) {
    const key = monthKey(lullaby.date);
    if (!groups.has(key)) groups.set(key, { key, label: monthLabel(key), items: [] });
    groups.get(key).items.push(lullaby);
  }
  return [...groups.values()];
}

const rubricPillClass = (active) =>
  `w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg transition-colors text-sm font-medium border ${
    active
      ? "bg-amber/15 text-amber-soft border-amber/25"
      : "text-muted-foreground hover:bg-night-card hover:text-cream border-transparent"
  }`;

const LullabiesPage = () => {
  useSeo({
    title: "Колыбельные на ночь — тексты и озвучка | Сказки детям на ночь",
    description:
      "Тихие песни на ночь: текст, озвучка и видео. Ищите по названию или выбирайте тему.",
    path: "/lullabies",
  });
  const [state, setState] = useState({ loading: true, error: "", lullabies: [] });
  const [query, setQuery] = useState("");
  const [rubric, setRubric] = useState("");

  useEffect(() => {
    let alive = true;
    async function loadLullabies() {
      try {
        const response = await fetch(`${API_BASE}/api/lullabies?limit=60`);
        if (!response.ok) throw new Error("Не удалось загрузить колыбельные");
        const data = await response.json();
        if (alive) setState({ loading: false, error: "", lullabies: data.lullabies || [] });
      } catch (error) {
        if (alive) setState({ loading: false, error: friendlyError(error, "Колыбельные временно недоступны"), lullabies: [] });
      }
    }
    loadLullabies();
    return () => {
      alive = false;
    };
  }, []);

  const stats = useMemo(() => {
    const total = state.lullabies.length;
    const withVideo = state.lullabies.filter((lullaby) => lullaby.hasVideo).length;
    return { total, withVideo };
  }, [state.lullabies]);

  const rubrics = useMemo(() => {
    const counts = new Map();
    for (const lullaby of state.lullabies) {
      if (!lullaby.rubric) continue;
      counts.set(lullaby.rubric, (counts.get(lullaby.rubric) || 0) + 1);
    }
    return [...counts.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name, "ru"));
  }, [state.lullabies]);

  const filteredLullabies = useMemo(() => {
    const q = query.trim().toLowerCase();
    return state.lullabies.filter((lullaby) => {
      if (rubric && lullaby.rubric !== rubric) return false;
      if (!q) return true;
      return [lullaby.title, lullaby.excerpt, lullaby.theme, lullaby.rubric].some((field) => field && field.toLowerCase().includes(q));
    });
  }, [state.lullabies, query, rubric]);

  const lullabyGroups = useMemo(() => groupLullabiesByMonth(filteredLullabies), [filteredLullabies]);

  return (
    <div className="min-h-screen bg-night-900 text-cream">
      <Header />
      <div className="max-w-7xl mx-auto lg:flex">
        {rubrics.length ? (
          <aside className="hidden lg:block w-64 shrink-0 border-r border-line sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto px-4 py-8">
            <h2 className="font-serif text-xl text-cream px-4 mb-5">По темам</h2>

            <button onClick={() => setRubric("")} className={`${rubricPillClass(!rubric)} mb-1`}>
              <span className="flex items-center gap-3">
                <Library className="h-4 w-4" />
                Все колыбельные
              </span>
              <span className="text-xs tabular-nums">{state.lullabies.length}</span>
            </button>

            <div className="my-3 mx-4 h-px bg-line" />

            <ul className="space-y-1">
              {rubrics.map((item) => (
                <li key={item.name}>
                  <button onClick={() => setRubric(item.name)} className={rubricPillClass(rubric === item.name)}>
                    <span className="flex items-center gap-3 min-w-0">
                      <Moon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{item.name}</span>
                    </span>
                    <span className="text-xs tabular-nums">{item.count}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        ) : null}

        <main className="flex-1 min-w-0 animate-fade-slide px-4 sm:px-6 lg:px-10 py-10">
          <section className="mb-10">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber/15 border border-amber/25 mb-5">
              <Moon className="h-5 w-5 text-amber" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.24em] text-amber font-semibold mb-4">
              Каждый вечер
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] text-cream max-w-3xl">
              {rubric ? (
                <span className="text-amber-soft">{rubric}</span>
              ) : (
                <>
                  Колыбельные <span className="italic text-muted-foreground">на ночь</span>
                </>
              )}
            </h1>
            <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
              Тихие песни на ночь: текст, озвучка и видео. Ищите по названию или выбирайте тему.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-amber" />
                <span className="tabular-nums text-cream font-medium">{stats.total}</span> текстов
              </div>
              <div className="h-4 w-px bg-line-strong" />
              <div className="flex items-center gap-2">
                <PlayCircle className="h-4 w-4 text-amber" />
                <span className="tabular-nums text-cream font-medium">{stats.withVideo}</span> с видео
              </div>
            </div>
          </section>

          {state.lullabies.length ? (
            <section className="mb-8 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
              <div className="relative flex-1 max-w-xl">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-dim" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Найти колыбельную…"
                  className="w-full h-12 pl-11 pr-4 rounded-full bg-night-card border border-line-strong text-sm text-cream placeholder:text-dim focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/25 transition-colors"
                />
              </div>
              {rubrics.length ? (
                <select
                  value={rubric}
                  onChange={(e) => setRubric(e.target.value)}
                  className="lg:hidden h-11 rounded-full bg-night-card border border-line-strong text-sm text-cream px-4 focus:outline-none focus:border-amber sm:max-w-xs"
                >
                  <option value="">Все колыбельные</option>
                  {rubrics.map((item) => (
                    <option key={item.name} value={item.name}>
                      {item.name} · {item.count}
                    </option>
                  ))}
                </select>
              ) : null}
            </section>
          ) : null}

          {state.loading ? (
            <div className="flex min-h-52 items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Загружаю колыбельные
            </div>
          ) : state.error ? (
            <div className="rounded-2xl border border-amber/30 bg-amber/10 px-5 py-5 text-amber-soft">{state.error}</div>
          ) : filteredLullabies.length ? (
            <div>
              {lullabyGroups.map((group) => (
                <section key={group.key} className="mb-14">
                  <div className="flex items-center gap-4 mb-6">
                    <h2 className="font-serif text-2xl sm:text-3xl text-cream capitalize whitespace-nowrap">
                      {group.label}
                    </h2>
                    <div className="h-px flex-1 bg-gradient-to-r from-line-strong via-line-strong/40 to-transparent" />
                    <span className="text-xs text-dim tabular-nums">{group.items.length}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {group.items.map((lullaby) => (
                      <motion.div key={lullaby.sourceKey} {...revealProps} variants={fadeUp} className="flex">
                      <Link
                        to={`/lullabies/${lullaby.sourceKey}`}
                        className="group w-full bg-night-card rounded-3xl overflow-hidden border border-line hover:border-amber/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-16px_rgba(245,183,74,0.3)] flex flex-col"
                      >
                        <div className="relative w-full aspect-[4/3] overflow-hidden bg-night-900">
                          <img
                            src={lullaby.coverUrl || PLACEHOLDER_COVER}
                            alt={lullaby.title}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-night-900/85 via-transparent to-transparent pointer-events-none" />
                          <div className="absolute top-3 left-3 flex gap-1.5">
                            <span className="inline-flex items-center gap-1 rounded-full bg-night-950/75 backdrop-blur px-2 py-0.5 text-[10px] font-medium text-cream border border-line">
                              {lullaby.hasVideo ? <PlayCircle className="h-3 w-3" /> : <BookOpen className="h-3 w-3" />}
                              {lullaby.hasVideo ? "Видео" : "Текст"}
                            </span>
                          </div>
                          {lullaby.hasVideo ? (
                            <div className="absolute top-3 right-3">
                              <span className="inline-flex items-center gap-1 rounded-full bg-night-950/75 backdrop-blur px-2 py-0.5 text-[10px] font-medium text-cream border border-line">
                                <Eye className="h-3 w-3" />
                                {formatViews(lullaby.views)}
                              </span>
                            </div>
                          ) : null}
                          {lullaby.rubric ? (
                            <div className="absolute bottom-3 left-3 right-3">
                              <span className="inline-flex items-center rounded-full bg-amber backdrop-blur px-2.5 py-0.5 text-[10px] font-semibold text-night-900">
                                {lullaby.rubric}
                              </span>
                            </div>
                          ) : null}
                        </div>

                        <div className="p-5 flex flex-col flex-1 gap-3">
                          <div className="flex items-center gap-1.5 text-xs text-dim">
                            <CalendarDays className="h-3 w-3" />
                            <span>{shortDate(lullaby.date)}</span>
                          </div>
                          <h3 className="font-serif text-xl leading-tight text-cream line-clamp-2 group-hover:text-cream transition-colors">
                            {lullaby.title}
                          </h3>
                          {lullaby.excerpt ? (
                            <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">{lullaby.excerpt}</p>
                          ) : null}
                          <div className="mt-auto pt-2 text-xs text-amber font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                            Слушать колыбельную →
                          </div>
                        </div>
                      </Link>
                      </motion.div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          ) : state.lullabies.length ? (
            <div className="rounded-2xl border border-line bg-night-card p-6 text-muted-foreground">
              {query ? <>Ничего не нашлось по запросу «{query}».</> : "Ничего не нашлось по выбранной теме."}
            </div>
          ) : (
            <div className="mt-6 border border-dashed border-line-strong rounded-2xl px-8 py-16 text-center">
              <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber/15 border border-amber/25 mb-5">
                <Sparkles className="h-6 w-6 text-amber" />
              </div>
              <h2 className="font-serif text-2xl text-cream">Колыбельные скоро появятся</h2>
              <p className="mt-3 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                Новые ежедневные колыбельные будут появляться здесь после публикации в канале.
              </p>
              <a
                href={LULLABY_CHANNEL_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClick("lullaby_channel")}
                className="inline-flex items-center gap-2 mt-6 rounded-full bg-amber hover:bg-amber-hover text-night-900 px-6 h-11 text-sm font-medium transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Открыть MAX-канал
              </a>
            </div>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default LullabiesPage;
